import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import jwtDecode from 'jwt-decode';
import logger from '../utils/logger';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const isTokenValid = (token) => {
    if (!token) return false;
    try {
      const { exp } = jwtDecode(token);
      const expirationTime = (exp * 1000) - 60000; // 1 minute buffer
      return Date.now() < expirationTime;
    } catch (error) {
      logger.error('Error validating token:', error);
      return false;
    }
  };

  const [auth, setAuth] = useState(() => {
    try {
      const token = localStorage.getItem('token');
      const user = localStorage.getItem('user');
      const email = localStorage.getItem('email');
      const group = localStorage.getItem('group');
      
      return { 
        token, 
        user, 
        email, 
        group,
        isAuthenticated: !!token && isTokenValid(token) 
      };
    } catch (error) {
      logger.error('Error reading auth from localStorage:', error);
      return { token: null, user: null, email: null, group: null, isAuthenticated: false };
    }
  });

  const login = useCallback((token, user, email, group) => {
    try {
      localStorage.setItem('token', token);
      localStorage.setItem('user', user);
      localStorage.setItem('email', email);
      localStorage.setItem('group', group);
      
      setAuth({ 
        token, 
        user, 
        email, 
        group,
        isAuthenticated: true 
      });
      
      logger.info('User logged in successfully');
    } catch (error) {
      logger.error('Error during login:', error);
      throw error;
    }
  }, []);

  const logout = useCallback(() => {
    try {
      localStorage.clear();
      setAuth({ 
        token: null, 
        user: null, 
        email: null, 
        group: null,
        isAuthenticated: false 
      });
      
      logger.info('User logged out');
    } catch (error) {
      logger.error('Error during logout:', error);
    }
  }, []);

  const checkIsAuthenticated = useCallback(() => {
    return auth.isAuthenticated && isTokenValid(auth.token);
  }, [auth.isAuthenticated, auth.token]);

  // Check token validity periodically and update auth state from localStorage
  useEffect(() => {
    const checkAuth = () => {
      const token = localStorage.getItem('token');
      const user = localStorage.getItem('user');
      const email = localStorage.getItem('email');
      const group = localStorage.getItem('group');
      
      if (token && isTokenValid(token)) {
        setAuth(prev => ({
          token,
          user,
          email,
          group,
          isAuthenticated: true
        }));
      } else if (token && !isTokenValid(token)) {
        logger.warn('Token expired, clearing auth');
        localStorage.clear();
        setAuth({
          token: null,
          user: null,
          email: null,
          group: null,
          isAuthenticated: false
        });
      }
    };

    // Check immediately
    checkAuth();

    // Check periodically
    const interval = setInterval(checkAuth, 5000); // Every 5 seconds

    // Listen for storage changes (e.g., when user logs in from another tab)
    const handleStorageChange = () => checkAuth();
    window.addEventListener('storage', handleStorageChange);

    return () => {
      clearInterval(interval);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [auth.token]);

  return (
    <AuthContext.Provider value={{ ...auth, login, logout, isAuthenticated: checkIsAuthenticated }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;

